import { mapActions, mapGetters } from "vuex";
import types from "../store/types";
import { debounce } from "debounce";

export default {
  data() {
    return {
      autosaveTimeoutWarning: 0,
      autosaveTimeout: 0,
      autosaveCheckInterval: 20000,
      // Autosave timing defaults can be overridden temporarily by components
      defaultAutosaveIdleDelay: 4000, // baseline idle delay used for most fields
      autosaveIdleDelay: 4000, // wait this long after last keystroke before saving
      defaultMinAutosaveGap: 10000, // baseline minimum time between consecutive saves
      minAutosaveGap: 10000, // enforce minimum time between save attempts
      lastAutosaveAt: 0, // track when the last autosave completed
      hasPendingAutosave: false, // toggle so we do not save when no edits were made
      saveFunction: null,
      debouncedOnBlur: null,
      isFieldFocused: false,
    };
  },
  created() {},
  computed: {
    ...mapGetters([
      types.getters.expiryTime,
      types.getters.lastSaveTime,
      types.getters.isSaving,
      types.getters.isLoading,
    ]),
  },
  beforeDestroy() {
    clearTimeout(this.autosaveTimeoutWarning);
    clearTimeout(this.autosaveTimeout);
  },
  methods: {
    ...mapActions([
      types.actions.refreshAccessToken,
      types.actions.logout,
      types.actions.setLastSaveTime,
      types.actions.setIsSaving,
    ]),
    initializeAutosave(saveFunction, formRoot) {
      // TODO remove to allow autosave
      // return;
      this.saveFunction = saveFunction;
      this.scheduleAutosave(); // kick off initial timer so edits are eventually captured
      const rootElement = formRoot.$el || formRoot; // To allow formRoot to be set on VueComponents and normal DOM elements
      const textInputs = Array.from(
        rootElement.querySelectorAll(
          'input[type="text"],input[type="email"],input[type="date"],input[type="datetime"],input[type="month"],input[type="number"],input[type="tel"],input[type="url"],input[type="time"],input[type="search"],textarea',
        ),
      );
      const otherInputs = Array.from(
        rootElement.querySelectorAll('input:not([type="text"]),select'),
      );
      this.debouncedOnChange = debounce(this.onChange, 1000);
      textInputs.forEach((input) => {
        input.addEventListener("input", this.debouncedOnChange);
        input.addEventListener("blur", this.debouncedOnChange);
      });
      otherInputs.forEach((input) => {
        input.addEventListener("change", this.debouncedOnChange);
      });
    },
    /**
     * Clear the pending autosave timer and schedule the next attempt.
     * Components can pass a custom delay (in ms) to debounce aggressive saves.
     */
    scheduleAutosave(delay = this.autosaveCheckInterval) {
      clearTimeout(this.autosaveTimeout);
      this.autosaveTimeout = setTimeout(
        this.attemptAutosave.bind(this),
        delay,
      );
    },
    onChange() {
      this.isFieldFocused = false;
      this.hasPendingAutosave = true; // mark form dirty so the next idle window saves it
      this.scheduleAutosave(this.autosaveIdleDelay);
    },
    /**
     * Start autosave if possible
     */
    async attemptAutosave() {
      if (!this.saveFunction) {
        // No need to run any logic if save function is undefined
        return;
      }
      if (!this.hasPendingAutosave) {
        this.scheduleAutosave();
        return;
      }

      const now = Date.now();

      if (now - this.lastAutosaveAt < this.minAutosaveGap) {
        const nextDelay = Math.max(
          this.minAutosaveGap - (now - this.lastAutosaveAt),
          this.autosaveIdleDelay,
        );
        this.scheduleAutosave(nextDelay);
        return;
      }

      clearTimeout(this.autosaveTimeout);
      if (this.isSaving || this.isLoading) {
        // Currently saving already, no need to autosave
        console.log("Currently saving, skipping autosave");
        this.scheduleAutosave(this.autosaveIdleDelay);
        return;
      }
      console.log("Autosaving...");
      this[types.actions.setIsSaving]({ isSaving: true });
      if (this.saveFunction) {
        const saveCallSuccess = await this.saveFunction(true);
        if (saveCallSuccess) {
          const currentTime = new Date();
          this[types.actions.setLastSaveTime]({ lastSaveTime: currentTime });
        }
      }
      this[types.actions.setIsSaving]({ isSaving: false });
      this.lastAutosaveAt = Date.now();
      this.hasPendingAutosave = false;
      this.scheduleAutosave();
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/autosave.js